import { getRefreshToken } from "@/lib/storeManager";
import { throwError } from "@/lib/throwError";
import { get, post } from "@/services/axiosUitils";
import { AuthRes, SocialLoginRes } from "@/utils/types/auth";

type Credentials = { email: string; password: string };
type SignupCredentials = {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
};

class AuthService {
  login = async (credentials: Credentials): Promise<AuthRes> => {
    if (!credentials) {
      throwError("Credentials are required", "AuthService");
    }
    return await post<AuthRes>("/auth/login/", credentials);
  };

  signup = async (credentials: SignupCredentials): Promise<AuthRes> => {
    if (!credentials) {
      throwError("Credentials are required", "AuthService");
    }
    return await post<AuthRes>("/auth/", credentials);
  };

  logout = async (): Promise<void> => {
    const refresh = getRefreshToken();
    if (!refresh) {
      throwError("Refresh token is required", "AuthService");
    }
    await post("/auth/logout/", { refresh });
  };

  socialLogin = async (provider: string): Promise<SocialLoginRes> => {
    return await post<SocialLoginRes>(`/auth/${provider}/login/`);
  };

  forgotPassword = async (
    emailOrPayload: string | { email: string; is_pts?: boolean }
  ): Promise<any> => {
    let email: string;
    let is_pts: boolean | undefined;

    if (typeof emailOrPayload === "string") {
      email = emailOrPayload;
    } else {
      email = emailOrPayload.email;
      is_pts = emailOrPayload.is_pts;
    }

    if (!email) {
      throwError("Email is required", "AuthService");
    }

    const payload = { email };
    const config: any = {};

    if (is_pts !== undefined) {
      config.params = { is_pts };
    }

    // Use base URL from environment for forgot password endpoint
    return await post<any>(
      "/forgot-password-request/",
      payload,
      Object.keys(config).length > 0 ? config : undefined
    );
  };
  forgotPasswordReset = async (
    id: string,
    payload: {
      new_password: string;
      confirm_password: string;
    }
  ): Promise<any> => {
    if (!id) {
      throwError("Reset token ID is required", "AuthService");
    }
    // Use base URL from environment for forgot password reset endpoint
    return await post<any>(`/forgot-password-reset/${id}/`, {
      new_password: payload.new_password,
      confirm_password: payload.confirm_password,
    });
  };

  refreshToken = async (): Promise<AuthRes> => {
    const refresh = getRefreshToken()!;
    if (!refresh) {
      throwError("Refresh token is required", "AuthService");
    }
    return await post<AuthRes>(
      "/auth/token-refresh/",
      new URLSearchParams({ refresh }),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );
  };

  resetPassword = async (values: {
    current_password: string;
    new_password: string;
  }): Promise<any> => {
    if (!values.current_password || !values.new_password) {
      throwError(
        "Current password and new password are required",
        "AuthService"
      );
    }
    // Use base URL from environment for reset password endpoint
    return await post<any>("/auth/password/change/", values);
  };

  getZulipToken = async (): Promise<any> => {
    return await get<any>("/auth/zulip/zulip-token/");
  };
}

const instance = new AuthService();
export default instance;
